/**
 *
 * CommunicationDetailMobile
 *
 */

import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushState, loadCommunication, updateCommunication } from './actions';
import {
  // other
  makeSelectLoading,
  makeSelectError,
  makeSelectItemDetail,
  makeSelectUpdateLoading,
  makeSelectFirstRender,
} from './selectors';

// assets
import {
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import CustomIcons from 'components/CustomIcons';

// antd component
import { Layout, Empty } from 'antd';
const { Content } = Layout;
// components
import Preloader from 'components/Preloader';
// Communication components
import { CommunicationPreview } from 'components/Communications';
import { FBDocumentsPreview } from 'components/Communications';
// mobile communication components
import {
  FBFormsAnswer,
  FBAppointmentAnswer,
  FBAnswersFormsAppointment,
  DiscussionMobile,
  FBAdminAnswerCard,
} from 'mobile/components/Communications';

// hooks
import { useRefState } from 'utils/hooks';
// utils
import { parseQuery } from 'utils/history';
import { isDateBeforeDateBySeconds } from 'utils/moment/moment.utils';
import _local from 'utils/localStorageUtils';

export function CommunicationDetailMobile({
  // main data
  itemDetail,
  // states
  history,
  error,
  loading,
  firstRender,
  updateLoading,
  // actions
  loadCommunication,
  updateCommunication,
  flushState,
}) {
  useInjectReducer({ key: 'communicationDetailMobile', reducer });
  useInjectSaga({ key: 'communicationDetailMobile', saga });

  const { t, i18n } = useTranslation();

  const { ID: initId } = useParams();
  const location = useLocation();
  const searchParams = parseQuery(location.search) || null;

  const localUser = _local.getLocalUser();
  const localActiveChild = _local.getLocalChild();

  const [discussionTab, setDiscussionTab] = useState(false);
  const [currentElement, currentElementRef, setCurrentElemente] = useRefState(
    itemDetail,
  );

  const oldParams = location?.state?.oldParams;
  const goBack = location?.state?.goBack;
  const goBackTitle = location?.state?.goBackTitle;

  useEffect(() => {
    if (initId) {
      loadCommunication(initId, true);
    }

    return () => {
      setCurrentElemente({});
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // Get Comunication Data
  useEffect(() => {
    if (!!searchParams?.IDComm && !!localActiveChild?._id) {
      const iniChildId = searchParams?.childId || localActiveChild?._id;
      loadCommunication(searchParams?.IDComm, true, iniChildId);
    }
  }, [searchParams?.IDComm, localActiveChild?._id]);

  // update communication on first mount of component
  useEffect(() => {
    if (itemDetail?._id) {
      setCurrentElemente({ ...currentElement, ...itemDetail });
      const hasThisUser =
        !!itemDetail?.recipientsActions &&
        itemDetail?.recipientsActions?.find(
          el => el?.recipient == localUser?._id,
        );

      const adminAnserIsActual = isDateBeforeDateBySeconds(
        itemDetail?.adminAnswer?.adminAnswerDate,
        itemDetail?.adminAnswerReadDate,
      );
      const setAdminAnswerReadDate =
        !!adminAnserIsActual ||
        (!!itemDetail?.adminAnswer && !!!itemDetail?.adminAnswerReadDate);

      if (!!firstRender) {
        if (!itemDetail?.read || !!!hasThisUser || !!setAdminAnswerReadDate) {
          // set as Read
          const recipientsActions = itemDetail?.recipientsActions || [];
          const parentsArr = hasThisUser
            ? itemDetail?.recipientsActions
            : [
                ...recipientsActions,
                {
                  recipient: localUser?._id,
                  responseID: initId,
                  communication: itemDetail?.communication,
                  read: true,
                  readDate: new Date().toISOString(),
                },
              ];

          let bodyObj = {
            _ID: initId,
            read: true,
            readDate: new Date().toISOString(),
            recipientsActions: parentsArr,
          };
          if (!!setAdminAnswerReadDate) {
            bodyObj = {
              ...bodyObj,
              adminAnswerReadDate: new Date().toISOString(),
            };
          }

          updateCommunication( bodyObj );
        }
      }
    }
  }, [itemDetail]);

  const needAnswerHundle = useCallback(
    formName => {
      const answerKeys = currentElement?.answer?.[formName]?.data
        ? Object.keys(currentElement?.answer?.[formName]?.data)
        : [];
      const needAnswer = currentElement?.types?.filter(
        el =>
          (el == 'forms' || el == 'appointments') && !answerKeys.includes(el),
      );
      return needAnswer?.length > 0;
    },
    [currentElement],
  );

  const onSubmitCallback = useCallback(
    (formData, formName) => {
      const answer = currentElementRef?.current?.answer || {};
      const initAnswer = {
        recipient: localUser?._id,
        student: localActiveChild?._id,
        responseID: initId,
      }
      let elData = {
        ...currentElementRef?.current,
        answer: { ...answer },
      };
       // set as Parents Actions
      const hasThisUser = !!elData?.recipientsActions && (
        elData?.recipientsActions?.find(
          el => el?.recipient == localUser?._id,
        )
      ) || null;
      

      if (
        !!formData && !Array.isArray(formName) &&
        JSON.stringify(formData) !=
          JSON.stringify(currentElement?.answer?.[formName]?.data)
      ) {
        elData = {
          ...elData,
          answer: {
            ...answer,
            [formName]: { ...initAnswer, data: formData },
          }
        };
        if (!!hasThisUser) {
          const recipientsActions = elData?.recipientsActions || [];
          elData = {
            ...elData,
            recipientsActions: [
              ...recipientsActions,
              {
                ...hasThisUser,
                actionDate: new Date().toISOString(),
              },
            ],
          };
        }

        setCurrentElemente(elData);
        updateCommunication({
          _ID: currentElement?._ID,
          answer: elData?.answer,
          student: localActiveChild?._id,
        });
      } else if (!!formData && Array.isArray(formName)) {
        let formsNames = formName || [];
        let formDataKeys = Object.keys(formData);

        formsNames?.map(formName => {
          if (formDataKeys?.includes(formName)) {
            elData = {
              ...elData,
              answer: {
                ...answer,
                ...elData?.answer,
                [formName]: { ...initAnswer, data: formData[formName] },
              },
            };

            if (!!hasThisUser) {
              const recipientsActions = elData?.recipientsActions || [];
              elData = {
                ...elData,
                recipientsActions: [
                  ...recipientsActions,
                  {
                    ...hasThisUser,
                    actionDate: new Date().toISOString(),
                  },
                ],
              };
            }

          }
        });

        setCurrentElemente(elData);
        updateCommunication({
          _ID: currentElement?._ID,
          answer: elData?.answer,
          student: localActiveChild?._id,
        });
      }
    },
    [itemDetail, currentElement],
  );

  const renderDiscussionPage = useMemo(() => {
    return (
      <Content className="main-content-container communication-detail discussion-opened">
        <div className="communication-detail__header">
          <div className="go-back" onClick={() => setDiscussionTab(false)}>
            <ArrowLeftOutlined />{" "}
            {(itemDetail?.title && (
              <>
                {t("labels.communication")}:{" "}
                <span className="title">{itemDetail?.title}</span>
              </>
            )) ||
              t("labels.communication")}
          </div>
        </div>
        <div className="communication-detail__main">
          <DiscussionMobile
            discussionId={currentElement?.discussion}
            communicationId={currentElement._id}
            parentCommunicationID={initId}
          />
        </div>
      </Content>
    );
  }, [itemDetail, currentElement, initId, i18n?.language]);

  const renderTopPageHeader = useMemo(() => {
    return (
      <div className="communication-detail__header">
        <div
          className="go-back"
          onClick={() => {
            history.push({
              pathname: goBack ? goBack : `/communications`,
              search: "",
              hash: "",
              state: {
                oldParams: oldParams,
                goBack: "/communications",
                cameFrom: "single_communication",
                needRefresh: searchParams?.needRefresh,
              },
            });
          }}
        >
          <ArrowLeftOutlined />{" "}
          {goBackTitle ? goBackTitle : t("labels.communications")}
        </div>
        {!!itemDetail?.discussion && (
          <div className="go-chat" onClick={() => setDiscussionTab(true)}>
            <CustomIcons type="comments" />
          </div>
        )}
      </div>
    );
  }, [goBack, oldParams, goBackTitle, searchParams, itemDetail?.discussion, i18n?.language]);

  const renderPageHeader = useMemo(() => {
    return (
      <>
        {/* {currentElement?.expired && needAnswerHundle('appointments') && (
          <>
            <span className="expired-message">
              <ExclamationCircleOutlined />
              <span className="text">Le délai de réponse est expiré !</span>
            </span>
          </>
        )} */}
        <CommunicationPreview
          element={currentElement}
          hideForm={true}
          hideAppointment={true}
          hideDocument={true}
          hideDiscussion={false}
          mode="onlyRead"
          onViewDiscussionClick={() => setDiscussionTab(true)}
        />
      </>
    );
  }, [currentElement, needAnswerHundle, i18n?.language]);

  const renderForms = useMemo(() => { 
    if (currentElement?.types?.includes("forms") && currentElement?.types?.includes("appointments")) {
      return (
        <>
          {(!!currentElement?.forms && !!currentElement?.appointments) && (
            <FBAnswersFormsAppointment
              loading={updateLoading}
              data={{
                forms: currentElement?.forms,
                appointments: currentElement?.appointments,
                formAnswer: currentElement?.answer?.forms?.data,
                appointmentAnswer: currentElement?.answer?.appointments?.data,
              }}
              adminAnswer={currentElement?.adminAnswer}
              onSubmit={onSubmitCallback}
              actionRequired={needAnswerHundle('forms') && needAnswerHundle('appointments')}
            />
          )}
        </>
      );
    } else if ( currentElement?.types?.includes("forms") ) {
      return (
        <>
          {currentElement?.forms && (
            <FBFormsAnswer
              loading={updateLoading}
              forms={currentElement?.forms}
              values={currentElement?.answer?.forms?.data}
              onSubmit={onSubmitCallback}
              actionRequired={needAnswerHundle('forms')}
            />
          )}
        </>
      );
    } else if (currentElement?.types?.includes("appointments")) {
      return (
        <>
          {currentElement?.appointments && (
            <FBAppointmentAnswer
              loading={updateLoading}
              adminAnswer={currentElement?.adminAnswer}
              appointments={currentElement?.appointments}
              values={currentElement?.answer?.appointments?.data}
              onSubmit={onSubmitCallback}
              actionRequired={needAnswerHundle('appointments')}
            />
          )}
        </>
      );
    }
  }, [currentElement, updateLoading, needAnswerHundle, i18n?.language]);


  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.communication")}</title>
        <meta name="description" content="Description of Communication" />
      </Helmet>
      {discussionTab ? (
        renderDiscussionPage
      ) : (
        <Content className="main-content-container communication-detail">
          {renderTopPageHeader}
          <div className="communication-detail__main">
            {loading && !currentElement && <Preloader />}
            {!loading && currentElement && (
              <>
                {renderPageHeader}
                {!!currentElement?.adminAnswer && (
                  <FBAdminAnswerCard
                    currentElement={currentElement}
                    answeredRecipient={
                      currentElement?.answeredRecipientData?.[0]
                    }
                    adminAnswer={currentElement?.adminAnswer}
                  />
                )}
                {renderForms}
                {!!currentElement?.documents && (
                  <FBDocumentsPreview documents={currentElement?.documents} />
                )}
              </>
            )}
            {!loading && !currentElement && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("errors.noData")}
              />
            )}
          </div>
        </Content>
      )}
    </Layout>
  );
}

CommunicationDetailMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemDetail: makeSelectItemDetail(),
  updateLoading: makeSelectUpdateLoading(),
  firstRender: makeSelectFirstRender(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadCommunication: (commObjID, firstRender, childId) =>
      dispatch(loadCommunication(commObjID, firstRender, childId)),
    updateCommunication: itemData => dispatch(updateCommunication(itemData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(CommunicationDetailMobile);
